package com.evoapps.evoshift.ui.adaptive

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.IndicationNodeFactory
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.HoverInteraction
import androidx.compose.foundation.interaction.InteractionSource
import androidx.compose.foundation.interaction.PressInteraction
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.ContentDrawScope
import androidx.compose.ui.node.DelegatableNode
import androidx.compose.ui.node.DrawModifierNode
import com.evoapps.evoshift.ui.DeepNavy
import kotlinx.coroutines.launch
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEvent
import androidx.compose.ui.node.PointerInputModifierNode
import androidx.compose.ui.node.ModifierNodeElement
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyGridScope
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.LazyGridState
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlin.math.abs

/*
 * Building blocks for tablets and desktop browsers (SH-30). On phones each of them collapses to
 * the single column the screens always had, so phone layouts do not change.
 */

/** Width of one card column in card grids: two columns on a tablet, three on a desktop. */
val CardMinWidth = 340.dp

/**
 * Cards (people, requests, rosters, notifications) in as many columns as fit [minItemWidth]; one
 * column on a phone. Use instead of a LazyColumn of cards.
 *
 * With [maxWidth] the cards stay in a centred column of at most that width while the grid itself
 * fills the screen (SH-130), so the wheel and the trackpad scroll it over the empty sides too.
 */
@Composable
fun AdaptiveCardGrid(
    modifier: Modifier = Modifier,
    minItemWidth: Dp = CardMinWidth,
    contentPadding: PaddingValues = PaddingValues(16.dp),
    spacing: Dp = 12.dp,
    state: LazyGridState = rememberLazyGridState(),
    maxWidth: Dp = Dp.Unspecified,
    content: LazyGridScope.() -> Unit
) {
    @Composable
    fun Grid(padding: PaddingValues, gridModifier: Modifier) = LazyVerticalGrid(
        columns = GridCells.Adaptive(minItemWidth),
        modifier = gridModifier,
        state = state,
        contentPadding = padding,
        horizontalArrangement = Arrangement.spacedBy(spacing),
        verticalArrangement = Arrangement.spacedBy(spacing),
        content = content
    )
    if (maxWidth == Dp.Unspecified) {
        Grid(contentPadding, modifier)
    } else {
        CentredBox(maxWidth) { margin -> Grid(contentPadding.plusHorizontal(margin), modifier.fillMaxSize()) }
    }
}

/**
 * SH-201: a grid state that stays at the end of its list when the screen's height changes, as on
 * every trip to a screen without the bottom bar and back. The bar shrinks away while this screen
 * slides out and expands again while it comes back, so the grid is briefly taller than it was. At
 * the end of the list a taller grid has nothing more to show and scrolls back, and when the bar
 * returns the list stays there: it landed about a bar's height lower than it was left. Scrolled
 * to the end (by the person), it is kept at the end through such height changes; anywhere else
 * nothing changes. Whether it was at the end survives the trip with the screen's saved state.
 */
@Composable
fun rememberEndKeepingGridState(): LazyGridState {
    val state = rememberLazyGridState()
    var atEnd by rememberSaveable { mutableStateOf(false) }
    LaunchedEffect(state) {
        var lastHeight = -1
        snapshotFlow { Triple(state.layoutInfo.viewportSize.height, state.canScrollForward, state.canScrollBackward) }
            .collect { (height, canScrollForward, canScrollBackward) ->
                if (height <= 0) return@collect
                val resized = height != lastHeight
                lastHeight = height
                when {
                    // The height changed under a list left at its end: back to the end.
                    resized -> if (atEnd && canScrollForward) state.requestScrollToItem(state.layoutInfo.totalItemsCount - 1)
                    // Same height: the person scrolled (or the content changed); remember where.
                    else -> atEnd = canScrollBackward && !canScrollForward
                }
            }
    }
    return state
}

/**
 * Two panes side by side on wide windows (e.g. a calendar and the day's shifts, a form's two
 * halves), stacked on phones and small tablets. [firstWeight] is the first pane's share.
 */
@Composable
fun TwoPane(
    modifier: Modifier = Modifier,
    firstWeight: Float = 0.5f,
    spacing: Dp = 24.dp,
    first: @Composable ColumnScope.() -> Unit,
    second: @Composable ColumnScope.() -> Unit
) {
    if (LocalWindowWidthClass.current.isExpanded) {
        Row(modifier = modifier, horizontalArrangement = Arrangement.spacedBy(spacing), verticalAlignment = Alignment.Top) {
            Column(Modifier.weight(firstWeight), content = first)
            Column(Modifier.weight(1f - firstWeight), content = second)
        }
    } else {
        Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(spacing)) {
            first()
            second()
        }
    }
}

/**
 * Dashboard sections in columns: one on a phone, two on a tablet, three on a desktop and four in
 * a very wide window (SH-98), never more than [maxColumns]. Each section is one card; they fill
 * the columns left to right, top to bottom, in order.
 */
@Composable
fun ResponsiveColumns(
    sections: List<@Composable () -> Unit>,
    modifier: Modifier = Modifier,
    spacing: Dp = 16.dp,
    maxColumns: Int = 3
) {
    val columns = minOf(maxColumns, columnsFor(LocalWindowWidthClass.current)).coerceAtLeast(1)
    if (columns == 1) {
        Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(spacing)) {
            sections.forEach { it() }
        }
        return
    }
    Row(modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(spacing)) {
        for (column in 0 until columns) {
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing)) {
                sections.forEachIndexed { index, section -> if (index % columns == column) section() }
            }
        }
    }
}

/** Columns of dashboard sections a window of [widthClass] holds (see [ResponsiveColumns]). */
fun columnsFor(widthClass: WindowWidthClass): Int = when (widthClass) {
    WindowWidthClass.COMPACT -> 1
    WindowWidthClass.MEDIUM -> 2
    WindowWidthClass.EXPANDED, WindowWidthClass.LARGE -> 3
    WindowWidthClass.EXTRA_LARGE -> 4
}

/**
 * Desktop affordance for clickable cards and rows: the hand cursor. The hover and press feedback
 * is the card's own [CardTint] (RegularCard, StandoutCard, [cardClickable]): until SH-130 this
 * also lifted the card with a scale and a shadow of a fixed shape, which did not match every
 * card's corners, shifted the layout and looked heavy.
 */
fun Modifier.hoverLift(): Modifier = handCursor()

/**
 * SH-130: the hover and press feedback of a clickable card, a tint of [color] over the whole
 * card (lighter on hover, a step stronger while pressed), instead of Material's ripple. It fills
 * the bounds it is drawn in, so the card clips it to its shape (a Card's content is clipped; a
 * custom card puts `clip(shape)` before `clickable`, see [cardClickable]).
 */
class CardTint(private val color: Color = DeepNavy) : IndicationNodeFactory {
    override fun create(interactionSource: InteractionSource): DelegatableNode = CardTintNode(interactionSource, color)
    override fun equals(other: Any?): Boolean = other is CardTint && other.color == color
    override fun hashCode(): Int = color.hashCode()

    companion object {
        /** Tint alphas: hovered and pressed. Focus is the [FocusRing] (SH-209), not a tint. */
        const val HOVER_ALPHA = 0.05f
        const val PRESSED_ALPHA = 0.10f
    }
}

private class CardTintNode(private val source: InteractionSource, private val color: Color) : Modifier.Node(), DrawModifierNode {
    private val alpha = Animatable(0f)

    override fun onAttach() {
        coroutineScope.launch {
            var hovers = 0
            var presses = 0
            source.interactions.collect { interaction ->
                when (interaction) {
                    is HoverInteraction.Enter -> hovers++
                    is HoverInteraction.Exit -> hovers--
                    is PressInteraction.Press -> presses++
                    is PressInteraction.Release, is PressInteraction.Cancel -> presses--
                }
                val target = when {
                    presses > 0 -> CardTint.PRESSED_ALPHA
                    hovers > 0 -> CardTint.HOVER_ALPHA
                    else -> 0f
                }
                launch { alpha.animateTo(target, tween(durationMillis = if (target > alpha.value) 90 else 160)) }
            }
        }
    }

    override fun ContentDrawScope.draw() {
        drawContent()
        val a = alpha.value
        if (a > 0f) drawRect(color = color, alpha = a)
    }
}

/**
 * A custom card (a Box or Row with its own background) that can be clicked: the hand cursor, and
 * the [CardTint] clipped to [shape], so hover and press tint exactly the card, and the keyboard
 * [focusRing] around it (SH-209). For Material's Card with the default (flat) elevation;
 * RegularCard and StandoutCard take `onClick` instead. Put it before any `clip(shape)` of the card
 * (a background takes the shape itself), or the clip cuts the ring off.
 */
fun Modifier.cardClickable(shape: Shape, tint: Color = DeepNavy, onClick: () -> Unit): Modifier =
    focusRing(shape)
        .handCursor()
        .clip(shape)
        .clickable(interactionSource = null, indication = CardTint(tint), onClick = onClick)

/** The hand cursor over something clickable that is not a card (chips, cells, links). */
fun Modifier.handCursor(): Modifier = pointerHoverIcon(PointerIcon.Hand)

/** The I-beam over text a person can select or type into. */
fun Modifier.textCursor(): Modifier = pointerHoverIcon(PointerIcon.Text)

/**
 * SH-98: a mouse wheel (or a two-finger swipe on a trackpad) over this element calls [onStep]
 * with +1 (down or right) or -1 (up or left), one step per wheel notch; a trackpad's small
 * deltas add up to a notch. The scroll is consumed, so a list under it does not move as well.
 */
fun Modifier.wheelSteps(onStep: (Int) -> Unit): Modifier = this then WheelStepsElement(onStep)

private data class WheelStepsElement(val onStep: (Int) -> Unit) : ModifierNodeElement<WheelStepsNode>() {
    override fun create() = WheelStepsNode(onStep)
    override fun update(node: WheelStepsNode) {
        node.onStep = onStep
    }
}

private class WheelStepsNode(var onStep: (Int) -> Unit) : Modifier.Node(), PointerInputModifierNode {
    private var accumulated = 0f

    override fun onPointerEvent(pointerEvent: PointerEvent, pass: PointerEventPass, bounds: IntSize) {
        if (pass != PointerEventPass.Main || pointerEvent.type != PointerEventType.Scroll) return
        val change = pointerEvent.changes.firstOrNull() ?: return
        val delta = change.scrollDelta.let { if (abs(it.y) >= abs(it.x)) it.y else it.x }
        accumulated += delta
        // One step per event at most: the month is read again before the next one.
        when {
            accumulated >= 1f -> { accumulated = 0f; onStep(1) }
            accumulated <= -1f -> { accumulated = 0f; onStep(-1) }
        }
        change.consume()
    }

    override fun onCancelPointerInput() {
        accumulated = 0f
    }
}
